import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, BookmarkIcon, CheckIcon, CopyIcon, EyeIcon, EyeOffIcon, GripVerticalIcon, LayersIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { newBlock, saveLanding, setLandingStatus } from '../../services/landingService';
import { newId } from '../../services/authService';
import { deleteComponent, instantiateComponent, saveComponent } from '../../services/componentService';
import { BLOCK_LIBRARY, blockDefinition } from '../../data/blockLibrary';
import { FONT_OPTIONS } from '../../data/fonts';
import { LandingBlock, LandingPage, LandingTheme } from '../../types';
import { BlockSettings } from '../../components/landings/BlockSettings';
import { StyleSettings } from '../../components/landings/StyleSettings';
import { LogoUploader } from '../../components/shared/LogoUploader';
import { DevicePreview, Device } from '../../components/shared/DevicePreview';
import { LandingRenderer } from '../../components/public/LandingRenderer';
import { ThankYouView } from '../../components/public/ThankYouView';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { Switch } from '../../components/ui/Switch';
import { Modal } from '../../components/ui/Modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { toast } from '../../components/ui/Toast';
import { copyToClipboard, landingUrl } from '../../utils/clipboard';
import { normalizeSlug, validateSlug } from '../../config/app';
const THEMES: {
  value: LandingTheme;
  label: string;
}[] = [{
  value: 'blanco_premium',
  label: 'Blanco premium'
}, {
  value: 'claro_corporativo',
  label: 'Claro corporativo'
}, {
  value: 'minimalista',
  label: 'Minimalista'
}];
export function LandingEditor() {
  const {
    id = ''
  } = useParams();
  const {
    organization
  } = useAuth();
  const {
    landings,
    forms,
    components
  } = useWorkspace();
  const stored = landings.find(l => l.id === id);
  const [draft, setDraft] = useState<LandingPage | null>(stored ?? null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  /** Solo se arrastra desde el asa, para no mover bloques por accidente. */
  const [handleId, setHandleId] = useState<string | null>(null);
  const [device, setDevice] = useState<Device>('desktop');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  /** Pestaña activa: con «gracias» la vista previa muestra la página de gracias. */
  const [tab, setTab] = useState('bloques');
  const [saveComponentFor, setSaveComponentFor] = useState<LandingBlock | null>(null);
  const [componentName, setComponentName] = useState('');
  useEffect(() => {
    if (stored && (!draft || draft.id !== stored.id)) setDraft(stored);
  }, [stored, draft]);
  if (!draft) {
    return <div className="rounded-2xl border border-border bg-background p-10 text-center" data-id="element-1072" data-mp="370kzh66sh95y">
        <p className="text-sm text-muted-foreground" data-id="element-1073" data-mp="3bxuxha88ut5e">Esta landing no existe.</p>
        <Link to="/app/landings" className="mt-4 inline-block" data-id="element-1074" data-mp="3gwfs8u4q8kmg">
          <Button variant="secondary" data-id="element-1075" data-mp="3lofx90znm3pw">Volver a landings</Button>
        </Link>
      </div>;
  }
  const form = forms.find(f => f.id === draft.formId);
  // Disponibilidad del enlace público: se comprueba contra el resto de landings.
  const slugCheck = validateSlug(draft.slug, landings.filter(l => l.id !== draft.id).map(l => l.slug));
  const blocks = draft.pageConfig.blocks;
  const selectedBlock = blocks.find(b => b.id === selectedBlockId) ?? null;
  function update(partial: Partial<LandingPage>) {
    setDraft(prev => prev ? {
      ...prev,
      ...partial
    } : prev);
    setSaved(false);
  }
  function updateConfig(partial: Partial<LandingPage['pageConfig']>) {
    update({
      pageConfig: {
        ...draft!.pageConfig,
        ...partial
      }
    });
  }
  function updateBlock(block: LandingBlock) {
    updateConfig({
      blocks: blocks.map(b => b.id === block.id ? block : b)
    });
  }
  function addBlock(block: LandingBlock) {
    updateConfig({
      blocks: [...blocks, block]
    });
    setSelectedBlockId(block.id);
    setAddOpen(false);
  }
  function duplicateBlock(block: LandingBlock) {
    const copy: LandingBlock = {
      ...(JSON.parse(JSON.stringify(block)) as LandingBlock),
      // El id debe ser un UUID válido: `landing_blocks.id` es de tipo uuid.
      id: newId('block')
    };
    const index = blocks.findIndex(b => b.id === block.id);
    const next = [...blocks];
    next.splice(index + 1, 0, copy);
    updateConfig({
      blocks: next
    });
  }
  /**
   * Reordena solo en vertical: se mueve el objeto completo del bloque, así que
   * conserva id, type, config, style, contenido y visibilidad.
   */
  function reorder(targetId: string) {
    setDragOverId(null);
    setHandleId(null);
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }
    const next = [...blocks];
    const fromIndex = next.findIndex(b => b.id === dragId);
    const toIndex = next.findIndex(b => b.id === targetId);
    next.splice(toIndex, 0, next.splice(fromIndex, 1)[0]);
    updateConfig({
      blocks: next
    });
    setDragId(null);
  }
  async function handleSave(status?: LandingPage['status']) {
    // El enlace público debe ser válido y único antes de guardar o publicar.
    const check = validateSlug(draft!.slug, landings.filter(l => l.id !== draft!.id).map(l => l.slug));
    if (!check.valid) {
      setSaveError(check.reason ?? 'El enlace no es válido.');
      toast.error('Revisa el enlace público', {
        description: check.reason
      });
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await saveLanding(draft!);
      if (status) {
        await setLandingStatus(draft!.id, status);
        update({
          status
        });
      }
      setSaved(true);
      if (status === 'publicada') {
        toast.success('Landing publicada correctamente', {
          description: landingUrl(draft!.slug)
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido.';
      setSaveError(message);
      toast.error('No se ha podido guardar', {
        description: message
      });
    } finally {
      setSaving(false);
    }
  }
  /** Copia la URL pública, con confirmación visible. */
  async function handleCopyLink() {
    const ok = await copyToClipboard(landingUrl(draft!.slug));
    if (ok) toast.success('Enlace copiado');else toast.error('No se ha podido copiar el enlace', {
      description: landingUrl(draft!.slug)
    });
  }
  async function confirmSaveComponent() {
    if (!saveComponentFor || !organization) return;
    await saveComponent(organization.id, componentName || blockDefinition(saveComponentFor.type).label, blockDefinition(saveComponentFor.type).label, saveComponentFor);
    setSaveComponentFor(null);
    setComponentName('');
  }
  return <>
      <Link to="/app/landings" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1076" data-mp="blu5kctb7ycl">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1077" data-mp="llkywb64w7b3" />
        Volver a landings
      </Link>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-1078" data-mp="qdcskukl6t5g">
        <div data-id="element-1079" data-mp="3p3cwq66yblh7">
          <h1 className="text-xl font-semibold tracking-tight text-navy sm:text-2xl" data-id="element-1080" data-mp="3p3ml5pcclkks">
            {draft.name}
          </h1>
          <p className="break-all text-sm text-muted-foreground" data-id="element-1081" data-mp="3u0d4cljg8waf">
            {landingUrl(draft.slug)} ·{' '}
            {draft.status === 'publicada' ? 'Publicada' : draft.status === 'borrador' ? 'Borrador' : 'Despublicada'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-1082" data-mp="1m1pj4rioxilc">
          {saving ? <span className="self-center text-sm text-muted-foreground" data-id="element-1083" data-mp="1lz3cqv7jd94p">
              Guardando…
            </span> : saved ? <span className="flex items-center gap-1.5 self-center text-sm text-green-600" data-id="element-1084" data-mp="1h4fx7luifp29">
              <CheckIcon className="h-4 w-4" data-id="element-1085" data-mp="1uzi000428yau" /> Guardado correctamente
            </span> : <span className="self-center text-sm text-muted-foreground" data-id="element-1086" data-mp="1qzrbk320au9h">
              Cambios sin guardar
            </span>}
          <Button variant="ghost" onClick={() => handleSave('borrador')} loading={saving} data-id="element-1087" data-mp="24tibqktul1sj">
            Guardar borrador
          </Button>
          <Button variant="secondary" leftIcon={<EyeIcon className="h-4 w-4" data-id="element-1089" data-mp="2eq3jvf15yt0l" />} onClick={() => window.open(`/landing/${draft.slug}`, '_blank')} data-id="element-1088" data-mp="2ep28tak5e6iv">
            Vista previa
          </Button>
          <Button variant="secondary" leftIcon={<CopyIcon className="h-4 w-4" data-id="element-1091" data-mp="2td268vl5yx8g" />} onClick={handleCopyLink} data-id="element-1090" data-mp="2tc0yazzg2quy">
            Copiar enlace
          </Button>
          <Button onClick={() => handleSave('publicada')} loading={saving} data-id="element-1092" data-mp="2xc1lpa4iuce5">
            {draft.status === 'publicada' ? 'Actualizar publicación' : 'Publicar'}
          </Button>
        </div>
      </div>

      {saveError && <div role="alert" className="mb-5 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between" data-id="element-1093" data-mp="2ofady0glx93q">
          <span data-id="element-1094" data-mp="2ehe91qe9jae2">
            <strong className="font-semibold" data-id="element-1095" data-mp="20nvc08sqstt0">Error al guardar. </strong>
            {saveError}
          </span>
          <Button variant="secondary" onClick={() => handleSave()} loading={saving} data-id="element-1096" data-mp="25mq09q9hbaki">
            Reintentar
          </Button>
        </div>}

      <div className="grid items-start gap-5 xl:grid-cols-[380px_minmax(0,1fr)]" data-id="element-1097" data-mp="1qr0ftwjxz5hw">
        {/* Panel izquierdo, con scroll propio para no perder la vista previa */}
        <div className="min-w-0 xl:max-h-[860px] xl:overflow-y-auto xl:pr-1" data-id="element-1098" data-mp="u7ghr6ufigsb">
          <Tabs defaultValue="bloques" value={tab} onValueChange={setTab} data-id="element-1099" data-mp="z72xbmxqc94a">
            <TabsList data-id="element-1100" data-mp="z4zv84jaypj7">
              <TabsTrigger value="bloques" data-id="element-1101" data-mp="k9trjo7oj2t1">Bloques</TabsTrigger>
              <TabsTrigger value="diseno" data-id="element-1102" data-mp="kbmy73ialn11">Diseño</TabsTrigger>
              <TabsTrigger value="gracias" data-id="element-1103" data-mp="p83mpytem3en">Gracias</TabsTrigger>
            </TabsList>

            <TabsContent value="bloques" data-id="element-1104" data-mp="1cxxxmau5xhw9">
              <p className="mb-3 text-xs text-muted-foreground" data-id="element-1105" data-mp="1cyzkdckjjstt">
                Haz clic en cualquier sección de la vista previa para editarla, o
                arrastra los bloques para reordenarlos.
              </p>
              <ul className="space-y-2" data-id="element-1106" data-mp="136ldy6iipy1b">
                {blocks.map(block => {
                const definition = blockDefinition(block.type);
                const active = selectedBlockId === block.id;
                return <li key={block.id} draggable={handleId === block.id} onDragStart={e => {
                  setDragId(block.id);
                  e.dataTransfer.effectAllowed = 'move';
                }} onDragEnd={() => {
                  setDragId(null);
                  setDragOverId(null);
                  setHandleId(null);
                }} onDragOver={e => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragId && dragId !== block.id) setDragOverId(block.id);
                }} onDragLeave={() => setDragOverId(prev => prev === block.id ? null : prev)} onDrop={() => reorder(block.id)} className={`relative rounded-xl border bg-background shadow-sm transition-colors ${active ? 'border-primary' : 'border-border'} ${dragId === block.id ? 'opacity-50' : ''} ${dragOverId === block.id ? 'before:absolute before:-top-1 before:left-0 before:h-0.5 before:w-full before:rounded-full before:bg-primary' : ''}`} data-id="element-1107" data-mp="20o1yiafyv3jk">
                      <div className="flex items-center gap-1 p-2.5" data-id="element-1108" data-mp="qrwwk1yoqxnr">
                        <span onPointerDown={() => setHandleId(block.id)} onPointerUp={() => setHandleId(null)} className="flex h-8 w-6 flex-none cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-1109" data-mp="mnxl3xs9ip19">
                          <GripVerticalIcon className="h-4 w-4" data-id="element-1110" data-mp="3pgllz306wrs1" />
                        </span>
                        <button type="button" onClick={() => setSelectedBlockId(active ? null : block.id)} className="min-w-0 flex-1 text-left" data-id="element-1111" data-mp="3tj8hbdw2kutz">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1112" data-mp="2wu0ve4rjgjpj">
                            {definition.label}
                          </span>
                          <span className="block truncate text-xs text-muted-foreground" data-id="element-1113" data-mp="35s30owib1yim">
                            {definition.description}
                          </span>
                        </button>
                        <button type="button" aria-label="Guardar en la biblioteca" onClick={() => {
                      setSaveComponentFor(block);
                      setComponentName(definition.label);
                    }} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1114" data-mp="2e16ntvw8vtvc">
                          <BookmarkIcon className="h-4 w-4" data-id="element-1115" data-mp="25m90c3x8jpqj" />
                        </button>
                        <button type="button" aria-label={block.visible ? 'Ocultar bloque' : 'Mostrar bloque'} onClick={() => updateBlock({
                      ...block,
                      visible: !block.visible
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1116" data-mp="21iayhlgj00ts">
                          {block.visible ? <EyeIcon className="h-4 w-4" data-id="element-1117" data-mp="1mt55if8a76uc" /> : <EyeOffIcon className="h-4 w-4" data-id="element-1118" data-mp="1hv3n7wiojbjx" />}
                        </button>
                        <button type="button" aria-label="Duplicar bloque" onClick={() => duplicateBlock(block)} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-1119" data-mp="1dtrvnlg8wg93">
                          <CopyIcon className="h-4 w-4" data-id="element-1120" data-mp="18wrkiw4jgnb4" />
                        </button>
                        {definition.removable && <button type="button" aria-label="Eliminar bloque" onClick={() => updateConfig({
                      blocks: blocks.filter(b => b.id !== block.id)
                    })} className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-1121" data-mp="33xv3cqdgpamn">
                            <Trash2Icon className="h-4 w-4" data-id="element-1122" data-mp="2u67ij8beuru5" />
                          </button>}
                      </div>

                      {active && <div className="border-t border-border p-4" data-id="element-1123" data-mp="2l6u5y6rzpn57">
                          <Tabs defaultValue="contenido" data-id="element-1124" data-mp="2l9q4nxbnwfup">
                            <TabsList data-id="element-1125" data-mp="2p9qhxen0yl06">
                              <TabsTrigger value="contenido" data-id="element-1126" data-mp="2p6m9g52j133p">Contenido</TabsTrigger>
                              <TabsTrigger value="estilo" data-id="element-1127" data-mp="2bbadxvgjr8lq">Estilo</TabsTrigger>
                            </TabsList>
                            <TabsContent value="contenido" data-id="element-1128" data-mp="2ga3phic9wpj9">
                              <BlockSettings block={block} onChange={updateBlock} data-id="element-1129" data-mp="9icf9j039lsv" />
                            </TabsContent>
                            <TabsContent value="estilo" data-id="element-1130" data-mp="eetbz1lct8hk">
                              <StyleSettings block={block} onChange={updateBlock} data-id="element-1131" data-mp="1yzzummychv9t" />
                            </TabsContent>
                          </Tabs>
                        </div>}
                    </li>;
              })}
              </ul>
              <Button className="mt-4 w-full" variant="secondary" leftIcon={<PlusIcon className="h-4 w-4" data-id="element-1133" data-mp="23x9gjsmizosw" />} onClick={() => setAddOpen(true)} data-id="element-1132" data-mp="28quexls16ksm">
                Añadir sección
              </Button>
            </TabsContent>

            <TabsContent value="diseno" data-id="element-1134" data-mp="2cqlf2g3g6d9t">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1135" data-mp="2cpjspfgze4cs">
                <Input id="l-name" label="Nombre interno" value={draft.name} onChange={e => update({
                name: e.target.value
              })} data-id="element-1136" data-mp="2rmav7xlybw6m" />
                <div data-id="element-1137" data-mp="x9b8zln2g3qd">
                  <Input id="l-slug" label="Slug (enlace público)" helperText={landingUrl(draft.slug)} value={draft.slug} error={slugCheck.valid ? undefined : slugCheck.reason} onChange={e => update({
                  slug: normalizeSlug(e.target.value)
                })} data-id="element-1138" data-mp="x8t5c3y2qn8f" />
                  {slugCheck.valid && <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-green-600" data-id="element-1139" data-mp="1lzgy5hvjd3vt">
                      <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1140" data-mp="1qz25afkv0ijm" />
                      Enlace disponible
                    </p>}
                </div>
                <Select id="l-form" label="Formulario asociado" placeholder="Sin formulario" options={forms.map(f => ({
                value: f.id,
                label: f.name
              }))} value={draft.formId ?? ''} onChange={e => update({
                formId: e.target.value || undefined
              })} data-id="element-1141" data-mp="20xhozxhk4pj3" />
                <Select id="l-theme" label="Tema visual" options={THEMES} value={draft.pageConfig.theme} onChange={e => updateConfig({
                theme: e.target.value as LandingTheme
              })} data-id="element-1142" data-mp="2epfrhpgrjn6a" />
                <Input id="l-color" label="Color principal" type="color" value={draft.pageConfig.primaryColor} onChange={e => updateConfig({
                primaryColor: e.target.value
              })} data-id="element-1143" data-mp="2omapm794jlk1" />
                <Select id="l-font" label="Tipografía" options={FONT_OPTIONS} value={draft.pageConfig.fontFamily} onChange={e => updateConfig({
                fontFamily: e.target.value
              })} data-id="element-1144" data-mp="7zm6gglugade" />
                <div data-id="element-1145" data-mp="3u8x3s8hh8kgs">
                  <p className="mb-2 text-sm font-medium text-foreground" data-id="element-1146" data-mp="3u9oryy8qvfx1">
                    Logotipo de la landing
                  </p>
                  <LogoUploader value={draft.pageConfig.logoUrl} onChange={value => updateConfig({
                  logoUrl: value
                })} data-id="element-1147" data-mp="qrf08d8qlzm6" />
                  <p className="mt-2 text-xs text-muted-foreground" data-id="element-1148" data-mp="lyu5n9i0bihl">
                    Si lo dejas vacío se usa el logotipo de tu empresa.
                  </p>
                </div>
              </div>

              {/* Biblioteca de componentes */}
              <div className="mt-5 rounded-2xl border border-border bg-background p-5" data-id="element-1149" data-mp="19phj0aaoz0bj">
                <div className="mb-3 flex items-center gap-2" data-id="element-1150" data-mp="i2oiqb8t05b8">
                  <LayersIcon className="h-4 w-4 text-primary" aria-hidden="true" data-id="element-1151" data-mp="i152ulr27w9z" />
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-1152" data-mp="mupqpkyxv9vg">
                    Biblioteca de componentes
                  </h3>
                </div>
                {components.length === 0 ? <p className="text-sm text-muted-foreground" data-id="element-1153" data-mp="e1mgrmni5f5k">
                    Guarda cualquier bloque con el icono de marcador para reutilizarlo
                    en otras landings.
                  </p> : <ul className="space-y-2" data-id="element-1154" data-mp="15qza8nsm0smu">
                    {components.map(component => <li key={component.id} className="flex items-center gap-2 rounded-xl border border-border p-3" data-id="element-1155" data-mp="rsrbcac0jh91">
                        <span className="min-w-0 flex-1" data-id="element-1156" data-mp="wq9m869lc6jo">
                          <span className="block truncate text-sm font-medium text-foreground" data-id="element-1157" data-mp="3cigp0k0zcx05">
                            {component.name}
                          </span>
                          <span className="block text-xs text-muted-foreground" data-id="element-1158" data-mp="3hfgtiqi6lrp5">
                            {component.category}
                          </span>
                        </span>
                        <Button size="sm" variant="secondary" onClick={() => addBlock(instantiateComponent(component))} data-id="element-1159" data-mp="34yo8840g9sbb">
                          Insertar
                        </Button>
                        <button type="button" aria-label="Eliminar componente" onClick={() => deleteComponent(component.id)} className="rounded-md p-2 text-muted-foreground hover:text-red-600" data-id="element-1160" data-mp="3e05i34n9kxrk">
                          <Trash2Icon className="h-4 w-4" data-id="element-1161" data-mp="2ma135li9x2xq" />
                        </button>
                      </li>)}
                  </ul>}
              </div>
            </TabsContent>

            <TabsContent value="gracias" data-id="element-1162" data-mp="2v83a0hooupsx">
              <div className="space-y-4 rounded-2xl border border-border bg-background p-5" data-id="element-1163" data-mp="2v71qdotlwuzl">
                <Input id="t-title" label="Título" value={draft.pageConfig.thankYou.title} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  title: e.target.value
                }
              })} data-id="element-1164" data-mp="27hh16o6v11hv" />
                <Input id="t-subtitle" label="Subtítulo" value={draft.pageConfig.thankYou.subtitle ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  subtitle: e.target.value
                }
              })} data-id="element-1165" data-mp="u2somyjt7pu6" />
                <Textarea id="t-text" label="Texto" rows={3} value={draft.pageConfig.thankYou.text} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  text: e.target.value
                }
              })} data-id="element-1166" data-mp="1hph8vqwajudy" />
                <Switch id="t-video" label="Mostrar vídeo" checked={Boolean(draft.pageConfig.thankYou.showVideo)} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showVideo: checked
                }
              })} data-id="element-1167" data-mp="1mplpri142r4p" />
                <Input id="t-videourl" label="URL del vídeo (YouTube, Vimeo, Wistia o mp4)" value={draft.pageConfig.thankYou.videoUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoUrl: e.target.value
                }
              })} data-id="element-1168" data-mp="2408wbce3jdjq" />
                <Input id="t-videothumb" label="Miniatura del vídeo (URL)" value={draft.pageConfig.thankYou.videoThumbnailUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  videoThumbnailUrl: e.target.value
                }
              })} data-id="element-1169" data-mp="1z6whftz7yl46" />
                <Input id="t-image" label="Imagen (URL)" value={draft.pageConfig.thankYou.imageUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  imageUrl: e.target.value
                }
              })} data-id="element-1170" data-mp="1bcwhxpi3ac1i" />
                <Switch id="t-cal" label="Mostrar botón de agendar llamada" checked={draft.pageConfig.thankYou.showCalendly} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showCalendly: checked
                }
              })} data-id="element-1171" data-mp="3ala6311sr4l9" />
                <Input id="t-calurl" label="URL de Calendly" value={draft.pageConfig.thankYou.calendlyUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  calendlyUrl: e.target.value
                }
              })} data-id="element-1172" data-mp="2v6porgqox5t7" />
                <Switch id="t-wa" label="Mostrar botón de WhatsApp" checked={draft.pageConfig.thankYou.showWhatsapp} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showWhatsapp: checked
                }
              })} data-id="element-1173" data-mp="3i6x7eu9orzdn" />
                <Input id="t-wanum" label="Número de WhatsApp" value={draft.pageConfig.thankYou.whatsappNumber ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  whatsappNumber: e.target.value
                }
              })} data-id="element-1174" data-mp="9qtzqoikqapb" />
                <Switch id="t-home" label="Mostrar botón de volver al inicio" checked={draft.pageConfig.thankYou.showHome} onChange={checked => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  showHome: checked
                }
              })} data-id="element-1175" data-mp="21eijlssu4v6s" />
                <Input id="t-redirect" label="Redirección automática (URL)" value={draft.pageConfig.thankYou.autoRedirectUrl ?? ''} onChange={e => updateConfig({
                thankYou: {
                  ...draft.pageConfig.thankYou,
                  autoRedirectUrl: e.target.value
                }
              })} data-id="element-1176" data-mp="2gb0x5koerr92" />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Vista previa editable, fija mientras el panel izquierdo se desplaza */}
        <div className="h-[860px] min-w-0 xl:sticky xl:top-4" data-id="element-1177" data-mp="2lfbl1ouzs3nx">
          <DevicePreview device={device} onDeviceChange={setDevice} toolbar={tab === 'gracias' ? <span className="text-xs text-muted-foreground" data-id="element-1179" data-mp="2gensuzv11gs4">
                  Vista previa de la página de gracias
                </span> : selectedBlock ? <span className="text-xs text-muted-foreground" data-id="element-1180" data-mp="2q60ayzr0wsky">
                  Editando: {blockDefinition(selectedBlock.type).label}
                </span> : <span className="text-xs text-muted-foreground" data-id="element-1181" data-mp="22h7mfocqdn0d">
                  Haz clic en una sección para editarla
                </span>} data-id="element-1178" data-mp="2ldgv1w4v5lcz">
            {tab === 'gracias' ? <ThankYouView config={draft.pageConfig.thankYou} logoUrl={draft.pageConfig.logoUrl ?? organization?.brand.logoUrl ?? organization?.logoUrl} primaryColor={draft.pageConfig.primaryColor} preview data-id="element-1182" data-mp="2cfexxro93cuz" /> : <LandingRenderer landing={draft} form={form} preview selectedBlockId={selectedBlockId} onSelectBlock={setSelectedBlockId} viewport={device === 'mobile' || device === 'iphone16' ? 'mobile' : device === 'tablet' ? 'tablet' : 'desktop'} data-id="element-1183" data-mp="3ntir4vn0kwyz" />}
          </DevicePreview>
        </div>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Añadir sección" data-id="element-1184" data-mp="3ve34aooyu3f5">
        {components.length > 0 && <div className="mb-6" data-id="element-1185" data-mp="3hf3e3clt1ggv">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1186" data-mp="3hg4u7vhogt5g">
              Mis componentes
            </p>
            <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1187" data-mp="czk3kaf9mveo">
              {components.map(component => <li key={component.id} data-id="element-1188" data-mp="hyn8luv8x1mn">
                  <button type="button" onClick={() => addBlock(instantiateComponent(component))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1189" data-mp="hyexa7eru4dk">
                    <span className="block text-sm font-medium text-foreground" data-id="element-1190" data-mp="870rxj052aci">
                      {component.name}
                    </span>
                    <span className="block text-xs text-muted-foreground" data-id="element-1191" data-mp="10po8cl46x179">
                      {component.category}
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>}
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground" data-id="element-1192" data-mp="16l4iu2ya1bru">
          Secciones disponibles
        </p>
        <ul className="grid gap-2 sm:grid-cols-2" data-id="element-1193" data-mp="11mjhu1b7ao2c">
          {BLOCK_LIBRARY.map(definition => <li key={definition.type} data-id="element-1194" data-mp="wnq2s4g9mp02">
              <button type="button" onClick={() => addBlock(newBlock(definition.type))} className="w-full rounded-xl border border-border p-3 text-left transition-colors hover:border-primary hover:bg-surface" data-id="element-1195" data-mp="rn2dbn7oqg7y">
                <span className="block text-sm font-medium text-foreground" data-id="element-1196" data-mp="itfm8aefa153">
                  {definition.label}
                </span>
                <span className="block text-xs text-muted-foreground" data-id="element-1197" data-mp="dwyxbdzefiqy">
                  {definition.description}
                </span>
              </button>
            </li>)}
        </ul>
      </Modal>

      <Modal open={Boolean(saveComponentFor)} onClose={() => setSaveComponentFor(null)} title="Guardar en la biblioteca" footer={<>
            <Button variant="ghost" onClick={() => setSaveComponentFor(null)} data-id="element-1199" data-mp="3ux8hc8a40l7n">
              Cancelar
            </Button>
            <Button onClick={confirmSaveComponent} data-id="element-1200" data-mp="3qxnox7fimqc">Guardar componente</Button>
          </>} data-id="element-1198" data-mp="3xq4iog9sa66">
        <Input id="component-name" label="Nombre del componente" value={componentName} onChange={e => setComponentName(e.target.value)} data-id="element-1201" data-mp="8qtoyooqs027" />
        <p className="mt-3 text-sm text-muted-foreground" data-id="element-1202" data-mp="379h7dbc1kk9j">
          Se guardará con su contenido y su estilo actual para que puedas reutilizarlo
          en cualquier otra landing de tu empresa.
        </p>
      </Modal>
    </>;
}